import React, { useEffect, useRef } from 'react';

import { Form, message } from 'antd';
import { I18n } from '@aws-amplify/core';
import moment from 'moment';

import { useApplicationContext } from '@/context/v1/Application/context';
import { useKimLhc } from '@/context/KimLhc';
import { useGetKimLhc } from '@/hooks/useGetKimLhc';
import { Footer } from '@/components/views/Report/KimLhc/Report/Footer';
import { useGeneralInformation } from '@/components/views/Report/KimLhc/Report/Steps/GeneralInformation/hooks';
import { useFrequency } from '@/components/views/Report/KimLhc/Report/Steps/Frequency/hooks';
import { usePosture } from '@/components/views/Report/KimLhc/Report/Steps/Posture/hooks';
import { useWorkCondition } from '@/components/views/Report/KimLhc/Report/Steps/WorkCondition/hooks';
import { useWorkOrganization } from '@/components/views/Report/KimLhc/Report/Steps/WorkOrganization/hooks';
import { useResult } from '@/components/views/Report/KimLhc/Report/Steps/Result/hooks';
import { useUpdateStepAndCommentKimLhcReport } from '@/hooks/useUpdateStepAndCommentKimLhcReport';

import { FormFields } from '@/components/views/Report/KimLhc/types';
import { CustomFormContainer } from '@/components/views/Report/KimLhc/Report/styles';
import type { Steps } from '@/context/KimLhc/types';

interface ContentStepProps {
	steps: Steps[];
}

export function ContentStep({ steps }: Readonly<ContentStepProps>): JSX.Element {
	const { form, file_id, currentStep, onSetStep, onSetReportId, report_id } = useKimLhc();
	const { organization, company } = useApplicationContext();
	const currentReportId = useRef<string | undefined>(undefined);

	const {
		data: report,
		isSuccess,
		isLoading
	} = useGetKimLhc({
		file_id: file_id || '',
		company_id: company?.id || '',
		organization_id: organization?.id || ''
	});

	const { handleSubmit: handleGeneralInfoSubmit, isLoading: isGeneralInfoLoading } = useGeneralInformation();
	const { handleSubmit: handleFrequencySubmit, isLoading: isFrequencyLoading } = useFrequency();
	const { handleSubmit: handlePostureSubmit, isLoading: isPostureLoading } = usePosture();
	const { handleSubmit: handleWorkConditionSubmit, isLoading: isWorkConditionLoading } = useWorkCondition();
	const { handleSubmit: handleWorkOrganizationSubmit, isLoading: isWorkOrganizationLoading } = useWorkOrganization();
	const { handleSubmit: handleResultSubmit, isLoading: isResultLoading } = useResult();

	const { mutateAsync: updateStepAndCommentKimLhcReport, isLoading: isUpdateStepAndCommentKimLhcReportLoading } =
		useUpdateStepAndCommentKimLhcReport();

	async function handleFormSubmit(values: FormFields): Promise<void> {
		const handlers: Record<number, (values: FormFields) => Promise<void>> = {
			0: handleGeneralInfoSubmit,
			1: handleFrequencySubmit,
			2: handlePostureSubmit,
			3: handleWorkConditionSubmit,
			4: handleWorkOrganizationSubmit,
			5: handleResultSubmit
		};

		const handler = handlers[currentStep];
		if (handler) {
			await handler(values);
		}
	}

	function handleFormSubmitFailed(): void {
		message.error(I18n.get('To proceed, please fill in all required information'));
	}

	function getIsLoading(): boolean {
		const loadingStates: Record<number, boolean> = {
			0: isGeneralInfoLoading,
			1: isFrequencyLoading,
			2: isPostureLoading,
			3: isWorkConditionLoading,
			4: isWorkOrganizationLoading,
			5: isResultLoading
		};

		return loadingStates[currentStep] ?? false;
	}

	async function updateStep(newStep: number): Promise<void> {
		if (!report_id || !file_id) {
			return;
		}

		const payload = {
			id: report_id,
			file_id: file_id,
			step: newStep,
			company_id: company?.id || '',
			organization_id: organization?.id || ''
		};

		await updateStepAndCommentKimLhcReport(payload);
		onSetStep(newStep);
	}

	function handlePreviousStep(): void {
		if (currentStep <= 0) {
			return;
		}

		const newStep = currentStep - 1;
		updateStep(newStep);
	}

	useEffect(() => {
		if ( currentReportId.current && currentReportId.current == report?.id) {
			return;
		}

		if (report && isSuccess) {

			const initialValues: FormFields = {
				general_information: {
					file_id: report?.file?.id,
					company_id: company?.id,
					organization_id: organization?.id,
					sector_id: report?.sector.id,
					line_id: report?.line.id,
					workstation_id: report?.workstation.id,
					activity_id: report?.activity.id,
					evaluator_id: report?.evaluator.id,
					collect_date: report?.collect_date ? moment(report.collect_date) : undefined,
					workday_duration: report?.day_duration,
					activity_duration: report?.activity_duration,
					company_name: company?.name,
					organization_name: organization?.name,
					sector_name: report?.sector?.name,
					line_name: report?.line?.name,
					workstation_name: report?.workstation?.name,
					activity_name: report?.activity?.name,
					evaluator_name: report?.evaluator?.name,
					file_name: report?.file?.original_name
				},
				frequency: {
					frequency: report?.frequency,
					weight: report?.weight,
					inclined_weight: report?.inclined_weight != null ? Boolean(report.inclined_weight) : undefined,
					transporting_cargo_in_pairs:
						report?.transporting_cargo_in_pairs != null
							? Boolean(report.transporting_cargo_in_pairs)
							: undefined,
					cargo_handling_condition: report?.cargo_handling_condition ?? undefined
				},
				posture: {
					body_posture: report?.body_posture ?? undefined,
					posture_additional_factor_twist: report?.posture_additional_factor_twist ?? undefined,
					posture_additional_factor_load_center: report?.posture_additional_factor_load_center ?? undefined,
					posture_additional_factor_arms: report?.posture_additional_factor_arms ?? undefined,
					posture_additional_factor_hands: report?.posture_additional_factor_hands ?? undefined
				},
				work_condition: {
					work_condition_hands_position: report?.work_condition_hands_position ?? undefined,
					work_condition_transfer: report?.work_condition_transfer ?? undefined,
					work_condition_spatial_condition: report?.work_condition_spatial_condition ?? undefined,
					work_condition_adverse: report?.work_condition_adverse ?? undefined,
					work_condition_clothes: report?.work_condition_clothes ?? undefined,
					work_condition_hold_load: report?.work_condition_hold_load ?? undefined
				},
				work_organization: {
					work_organization: report?.work_organization ?? undefined
				},
				result: {
					man_score: null,
					woman_score: null,
					man_risk_level: null,
					woman_risk_level: null,
					man_physical_overload_probability: null,
					man_health_consequences: null,
					man_measures: null,
					woman_physical_overload_probability: null,
					woman_health_consequences: null,
					woman_measures: null,
					comment: null
				}
			};

			form.setFieldsValue(initialValues);

			onSetReportId(report.id)
			currentReportId.current = report.id;

			onSetStep(report.step ?? 0);

		}
	}, [report?.id, isSuccess, company?.id, company?.name, organization?.id, organization?.name, form, report]);

	if (!company?.id || !organization?.id || isLoading || isUpdateStepAndCommentKimLhcReportLoading) {
		return <div>Loading...</div>;
	}

	return (
		<Form form={form} layout="vertical" onFinish={handleFormSubmit} onFinishFailed={handleFormSubmitFailed}>
			<CustomFormContainer xs={24}>{steps[currentStep]?.component}</CustomFormContainer>
			<Footer
				currentStep={currentStep}
				totalSteps={steps.length}
				onPrevious={handlePreviousStep}
				isLoading={getIsLoading()}
			/>
		</Form>
	);
}
